import React from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PLAN_LOOKUP_KEYS = {
  basic: "cv_basic",
  pro: "cv_pro",
  elite: "cv_elite",
};

export const PENDING_PLAN_KEY = "pending_plan";

/**
 * Starts a Stripe Checkout session for a plan.
 * Unauthenticated users are sent to login first; the landing page
 * auto-resumes checkout for them afterwards (see App.js).
 */
export function useCheckout() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loadingPlan, setLoadingPlan] = React.useState(null);

  const startCheckout = React.useCallback(
    async (plan) => {
      const lookupKey = PLAN_LOOKUP_KEYS[plan] || plan;
      if (user === null) return; // auth still loading
      if (user === false) {
        sessionStorage.setItem(PENDING_PLAN_KEY, lookupKey);
        toast.info("Sign in to continue", {
          description: "Log in or create an account — we'll take you straight to secure checkout after.",
        });
        navigate("/login?next=/");
        return;
      }
      setLoadingPlan(lookupKey);
      try {
        const { data } = await axios.post(
          `${API}/payments/checkout`,
          { lookup_key: lookupKey, origin_url: window.location.origin },
          { withCredentials: true }
        );
        window.location.href = data.checkout_url;
      } catch (err) {
        toast.error("Couldn't start checkout", {
          description: err?.response?.data?.detail || "Please try again in a moment.",
        });
        setLoadingPlan(null);
      }
    },
    [user, navigate]
  );

  return { startCheckout, loadingPlan };
}
